import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, CircleNotch, WarningCircle } from "@phosphor-icons/react";
import { API } from "@/lib/api";

/**
 * /payment/success — landing after Stripe Checkout. Polls
 * /api/payments/status/{session_id} every 2 s until the webhook (or
 * inline fallback) flips payment_status to "paid". Never advances past
 * success without the paid confirmation.
 */
const PaymentSuccess = () => {
  const [status, setStatus] = useState("polling");
  const [tries, setTries] = useState(0);
  const [msg, setMsg] = useState("Confirming your payment with Stripe…");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); setMsg("Missing session_id"); return; }

    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      setTries(attempts);
      if (attempts > 40) { // ~80 s hard cap
        if (!cancelled) { setStatus("timeout"); setMsg("Still processing. Refresh in a minute — your subscription will activate as soon as Stripe confirms."); }
        return;
      }
      try {
        const r = await axios.get(`${API}/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          if (!cancelled) {
            setStatus("paid");
            setMsg("Your subscription is active. Redirecting…");
            setTimeout(() => { window.location.href = "/"; }, 2000);
          }
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          if (!cancelled) { setStatus("failed"); setMsg("Payment didn't go through. No charge was made — try again from Credits & Keys."); }
          return;
        }
      } catch (e) { /* transient — keep polling */ }
      if (!cancelled) setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "polling" && (
          <>
            <CircleNotch size={48} className="animate-spin text-[#E91E8C] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-zinc-900">Almost there…</h1>
            <p className="text-sm text-zinc-500 mt-2">{msg}</p>
            <p className="text-[10px] text-zinc-400 mt-4">Check {tries}/40</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-zinc-900">You&apos;re in ✨</h1>
            <p className="text-sm text-zinc-600 mt-2">{msg}</p>
          </>
        )}
        {(status === "failed" || status === "error" || status === "timeout") && (
          <>
            <WarningCircle size={48} weight="fill" className="text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-zinc-900">Hmm — {status}</h1>
            <p className="text-sm text-zinc-500 mt-2">{msg}</p>
            <a href="/" className="btn-primary inline-block mt-4">Back to dashboard</a>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
